import { writeoffApi } from '../writeoffApi';
import {
  WriteoffView,
  WriteoffCreateRequest,
  WriteoffUpdateRequest,
  WriteoffWorklistView,
  WriteoffWorklistEndpointRequest,
  WriteoffSearchView,
  WriteoffSearchRequest,
} from '../types';

const writeoffEndpointsInjected = writeoffApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get single writeoff by ID
    // GET /WriteOff?id=<id>
    getWriteoff: builder.query<WriteoffView, string>({
      query: (id) => ({
        url: '/WriteOff',
        method: 'GET',
        params: { id },
      }),
      providesTags: ['Writeoff'],
    }),

    // Get writeoff worklist by type
    // GET /WriteoffWorklists?type=<type>&center=<center>&company=<company>
    getWriteoffWorklist: builder.query<WriteoffWorklistView[], WriteoffWorklistEndpointRequest>({
      query: ({ type, center, company }) => ({
        url: '/WriteoffWorklists',
        method: 'GET',
        params: { type, center, company },
      }),
      providesTags: (result, error, { type, center, company }) => [
        { type: 'WriteoffWorklist', id: `${type}-${center}-${company}` },
        'WriteoffWorklist',
      ],
    }),

    // Create writeoff
    // POST /WriteoffCreate
    createWriteoff: builder.mutation<WriteoffView, WriteoffCreateRequest>({
      query: (body) => ({
        url: '/WriteoffCreate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Writeoff', 'WriteoffWorklist'],
    }),

    // Update writeoff
    // POST /writeoffUpdate
    updateWriteoff: builder.mutation<WriteoffView, WriteoffUpdateRequest>({
      query: (body) => ({
        url: '/writeoffUpdate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Writeoff', 'WriteoffWorklist'],
    }),

    // Get writeoffs pending approval by user
    // GET /WriteoffWorklists?type=MyApprovals&center=<center>&company=<company>&approverName=<approverName>
    getWriteoffApprovalsByUser: builder.query<
      WriteoffWorklistView[],
      { center: string; company: string; userName: string }
    >({
      query: ({ center, company, userName }) => ({
        url: '/WriteoffWorklists',
        method: 'GET',
        params: { type: 'MyApprovals', center, company, approverName: userName },
      }),
      providesTags: (result, error, { center, company, userName }) => [
        { type: 'WriteoffWorklist', id: `MyApprovals-${center}-${company}-${userName}` },
        'WriteoffWorklist',
      ],
    }),

    // Search writeoffs by patient name, account number, or case ID
    // GET /WriteoffSearch?company=<company>&center=<center>&searchTerm=<searchTerm>
    searchWriteoffs: builder.query<WriteoffSearchView[], WriteoffSearchRequest>({
      query: ({ company, center, searchTerm }) => ({
        url: '/WriteoffSearch',
        method: 'GET',
        params: { company, center, searchTerm },
      }),
      providesTags: ['WriteoffWorklist'],
    }),
  }),
  overrideExisting: false,
});

// Export enhanced API for slice extraReducers
export const writeoffEndpoints = writeoffEndpointsInjected;

export const {
  useGetWriteoffQuery,
  useGetWriteoffWorklistQuery,
  useCreateWriteoffMutation,
  useUpdateWriteoffMutation,
  useGetWriteoffApprovalsByUserQuery,
  useSearchWriteoffsQuery,
} = writeoffEndpointsInjected;
